<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading || imagesSaving"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`pages.visibility.transfer.modal.confirm.title`) }}</h1>
      </modal-header>
    </template>
    <form @submit="save">
      <div class="mt-10">
        <div class="flex flex-col w-full">
          <form-card>
            <template #title>
              {{ $t(`pages.visibility.transfer.modal.confirm.pictures`) }}
            </template>
            <template #description>
              {{ $t(`pages.visibility.transfer.modal.confirm.description`) }}
            </template>
            <Images
              v-model="images"
              :aspect="0.5"
              :label="$t('pages.visibility.transfer.modal.confirm.uploadPictures')"
              @choose-file="openImageChooser"
            />
          </form-card>
          <form-card
            :title="$t('pages.visibility.transfer.modal.confirm.title')"
            :description="$t('pages.visibility.transfer.modal.confirm.description2')"
          >
            <div class="flex flex-col gap-y-6">
              <FormInput
                :model-value="rawVisibilityTransfer.visibilityPlanning.region.name"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.transfer.modal.confirm.region')"
                disabled
              />
              <FormInput
                :model-value="rawVisibilityTransfer.visibilityBicycle.number"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.transfer.modal.confirm.bicycle')"
                disabled
              />
              <Multiselect
                v-model="rawVisibilityTransfer.visibilityBicycle.status"
                :error="errors?.status"
                :options="fetchStatus"
                :label="$t('pages.visibility.transfer.modal.confirm.bicycleStatus')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <FormInput
                v-model="rawVisibilityTransfer.newDesign"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.transfer.modal.confirm.newDesign')"
              />
              <FormInput
                v-model="rawVisibilityTransfer.comment"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.transfer.modal.confirm.comment')"
              />
              <Switch
                v-model="rawVisibilityTransfer.newDesignAdded"
                :error="errors?.newDesignAdded"
                :label="$t('pages.visibility.transfer.modal.confirm.newDesignAdded')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, ref, type Ref,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  boolean, date, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import { Upload, useFileUpload } from '@utils/upload/fileUpload';
import Images from '@utils/inputs/Images.vue';
import FormInput from '@utils/inputs/Input.vue';
import Switch from '@utils/inputs/Switch.vue';
import Multiselect from '@utils/Multiselect.vue';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import type { VisibilityTransfer, WbFile } from '@/entities/Entities';

interface Props {
  open: boolean;
  visibilityTransfer?: VisibilityTransfer;
}

const emit = defineEmits(['update:open', 'update:visibilityTransfer', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityTransfer = useVModel(props, 'visibilityTransfer', emit) as Ref<VisibilityTransfer | undefined>;

const newDesignAdded = ref(false);

const { t } = useI18n();

const notify = useNotify();

const userLocation = ref<{ lat: number; long: number } | null>(null);

const getUserLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        userLocation.value = {
          lat: position.coords.latitude,
          long: position.coords.longitude,
        };
      },
      (error) => {
        console.error('Error obtaining location:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  } else {
    console.error('Geolocation is not supported by this browser.');
  }
};

getUserLocation();

const initialData = (visibilityTransfer?: VisibilityTransfer) => (visibilityTransfer
  ? {
    ...visibilityTransfer,
    newDesignAdded: false,
  }
  : {
    campaign: undefined,
    region: undefined,
    visibilityBicycle: undefined,
    transferTime: undefined,
    newLocation: undefined,
    responsibleHelper: undefined,
    completionPicture: undefined,
    newDesign: undefined,
    comment: undefined,
    done: false,
    newDesignAdded: false,
  });

const rawVisibilityTransfer = syncRef(internalVisibilityTransfer, (visibilityTransfer: VisibilityTransfer) => ({
  ...initialData(visibilityTransfer),
  region: visibilityTransfer?.region?.['@id'],
}));

const {
  files: images,
  open: openImageChooser,
  uploadDiff: uploadImageDiff,
  loading: imagesSaving,
  getUpdatedFiles,
  reset: resetImages,
} = useFileUpload<WbFile>(
  computed<any>(
    () => props.visibilityTransfer?.photos
      ?.filter((photo) => photo.file)
      ?.map((photo) => (photo.file ? Upload.from(photo.file) : null)) ?? [],
  ),
);

const {
  data: visibilityTransfer,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    transferTime: date()
      .nullable(),
    newDesign: string()
      .nullable(),
    comment: string()
      .nullable(),
    newDesignAdded: boolean()
      .oneOf([true], t('pages.visibility.transfer.modal.confirm.newDesignAddedValidation'))
      .required(),
  }),
  rawVisibilityTransfer,
);

function resetData() {
  rawVisibilityTransfer.value = { ...initialData() } as unknown as typeof visibilityTransfer['value'];
  setValidation(false);
  newDesignAdded.value = false;
  resetImages();
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityTransfer.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const fetchStatus = enumOptionsProvider('/enums/VisibilityBicycleStatus', (item) => ({
  value: item,
  label: t(`pages.visibility.bicycle.visibilityBicycleStatus.${item}`),
}));

const save = validatedFn(async () => {
  try {
    const additionalImages = await uploadImageDiff();

    if (!additionalImages?.length) {
      notify?.({
        variant: 'error',
        title: t('pages.visibility.modal.confirm.NoPhotoSaveError'),
      });
      return;
    }

    const newImages = getUpdatedFiles(additionalImages);

    // TODO check and change to something different than photo if wanted
    const visibilityPhotos = [];
    for (const photo of newImages) {
      visibilityPhotos.push(exec(
        '/visibility_photos',
        {
          method: 'POST',
          body: {
            file: photo.file['@id'],
            gisLocation: userLocation.value ?? undefined,
            visibilityTransfer: visibilityTransfer.value?.['@id'],
            creationTimestamp: new Date().toISOString(),
          },
        },
      ));
    }
    const newVisibilityPhotos = await Promise.all(visibilityPhotos);

    await exec(
      `${props.visibilityTransfer?.['@id']}/confirm`,
      {
        method: 'PUT',
        body: {
          transferTime: visibilityTransfer.value.transferTime,
          responsibleHelper: visibilityTransfer.value.responsibleHelper, // Assuming this is an ID
          photos: newVisibilityPhotos.filter((p) => p).map((p) => p['@id']),
          newDesign: visibilityTransfer.value.newDesign,
          comment: visibilityTransfer.value.comment,
          newBicycleStatus: visibilityTransfer.value.visibilityBicycle?.status,
        },
      },
    );

    notify?.({
      variant: 'success',
      title: t('pages.visibility.transfer.modal.confirm.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('pages.visibility.transfer.modal.confirm.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
